import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, Truck, RefreshCw, ChevronDown, Globe, Heart, Sparkles, Ruler, Zap, Scissors, Check, MapPin, Star } from "lucide-react";
import { api, money, cdnImg, cdnSrcSet } from "@/lib/api";
import { swatchBg, isLightColor } from "@/lib/colors";
import { sizeLabel } from "@/lib/sizes";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { ProductCard } from "@/components/ProductCard";
import { RecentlyViewed } from "@/components/RecentlyViewed";
import { TryOnModal } from "@/components/TryOnModal";
import { SizeGuideModal } from "@/components/SizeGuideModal";
import { AlterationFeeModal } from "@/components/AlterationFeeModal";
import { alterationEligible } from "@/lib/alterations";
import { ShareMenu } from "@/components/ShareMenu";
import { ReviewSection } from "@/components/ReviewSection";
import { addRecentlyViewed } from "@/lib/recentlyViewed";
import { Seo } from "@/components/Seo";
import { productSchema, breadcrumbSchema, SITE } from "@/lib/seo";
import { trackViewItem, trackContact } from "@/lib/analytics";
import { toast } from "sonner";

const Row = ({ label, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#E5E5DF]">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between py-4 text-left" data-testid={`accordion-${label.toLowerCase().replace(/\s/g, "-")}`}>
        <span className="font-body text-sm tracking-wide">{label}</span>
        <ChevronDown size={16} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-4 text-sm text-[#6E6E69] leading-relaxed">{children}</div>}
    </div>
  );
};

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem, addAlteration, updateNote, items, setOpen, rawSubtotal } = useCart();
  const { has, toggle } = useWishlist();
  const [data, setData] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  const galleryRef = useRef(null);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const [tryOn, setTryOn] = useState(false);
  const [sizeGuide, setSizeGuide] = useState(false);
  const [altFee, setAltFee] = useState(null);
  const [altOpen, setAltOpen] = useState(false);
  const [completeLook, setCompleteLook] = useState([]);
  const [showBar, setShowBar] = useState(false);
  const addRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setData(null); setActiveImg(0); setSize(null); setColor(null); setQty(1); setCompleteLook([]); setShowBar(false);
    api.get(`/products/${slug}`).then((r) => {
      setData(r.data); addRecentlyViewed(r.data.product); trackViewItem(r.data.product);
      const p = r.data.product;
      const jwl = /jewel|necklace|earring|bangle|bracelet|\bring\b|accessor|jhumka|maang|tikka|pendant|choker|anklet|\bkada\b|\bhaar\b|nose[- ]?pin/i.test([...(p.collections || []), p.title].join(" "));
      const compCol = jwl ? "sarees" : "jewelry-and-accessories";
      api.get(`/products?collection=${compCol}&limit=8`)
        .then((rr) => setCompleteLook(rr.data.filter((x) => x.slug !== p.slug).slice(0, 4)))
        .catch(() => {});
    }).catch(() => navigate("/shop"));
  }, [slug, navigate]);

  useEffect(() => {
    if (!data || !addRef.current) return;
    const obs = new IntersectionObserver(([e]) => setShowBar(!e.isIntersecting), { threshold: 0 });
    obs.observe(addRef.current);
    return () => obs.disconnect();
  }, [data]);

  // Flat $15 alteration fee info (hidden Shopify charge product).
  useEffect(() => {
    api.get("/alteration-fee").then((r) => setAltFee(r.data)).catch(() => setAltFee(null));
  }, []);

  if (!data) return <div className="min-h-[60vh] flex items-center justify-center"><span className="overline text-[#6E6E69]">Loading…</span></div>;
  const { product, related } = data;
  const onSale = product.compare_at > product.price;
  const sizes = product.sizes || [];
  const colors = product.colors || [];
  const variants = product.variants || [];
  const isJewelry = /jewel|necklace|earring|bangle|bracelet|\bring\b|accessor|jhumka|maang|tikka|pendant|choker|anklet|\bkada\b|\bhaar\b|nose[- ]?pin/i.test(
    [...(product.collections || []), product.title].join(" ")
  );
  // Bridesmaid pre-order pieces: made-to-order, WhatsApp checkout only
  const isPreorder = (product.collections || []).includes("pre-order-bridesmaid-dresses");
  const preorderWa = `https://wa.me/12242461461?text=${encodeURIComponent(
    `Hi Kurti Connection! I'd like to pre-order "${product.title}"${size ? ` (size ${size})` : ""} — quantity ${qty}, for an upcoming wedding. Please confirm group pricing and delivery timeline.\n${window.location.origin}/product/${product.slug}`
  )}`;

  const sizeAvailable = (s) =>
    !variants.length || variants.some((v) => v.size === s && (!color || v.color === color) && v.available);
  const colorAvailable = (c) =>
    !variants.length || variants.some((v) => v.color === c && (!size || v.size === size) && v.available);

  const pickColor = (c) => {
    setColor(c);
    if (size && !variants.some((v) => v.color === c && v.size === size && v.available)) setSize(null);
  };
  const pickSize = (s) => {
    setSize(s);
    if (color && !variants.some((v) => v.size === s && v.color === color && v.available)) setColor(null);
  };

  // Size remembered from the Outfit Finder quiz (localStorage kc_my_size).
  // Plain read, not a hook — this component has early returns above.
  const mySize = (() => {
    try { return JSON.parse(localStorage.getItem("kc_my_size")); } catch { return null; }
  })();
  const mySizeFit = !!(mySize && sizes.some(
    (s) => s.toLowerCase() === String(mySize.garment) || s.toLowerCase() === String(mySize.us).toLowerCase()
  ));

  const validate = () => {
    if (colors.length && !color) { toast.error("Please select a colour."); return false; }
    if (sizes.length && !size) { toast.error("Please select a size."); return false; }
    return true;
  };
  const add = () => {
    if (!validate()) return;
    addItem(product, size, qty, color);
  };
  // Flat $15 alteration: measurements form → garment + fee land in the bag,
  // note rides into the Shopify order note.
  const confirmFlatAlteration = (note) => {
    setAltOpen(false);
    if (!validate()) return;
    const garmentInBag = items.some((i) => !i.isAlteration && i.slug === product.slug && i.size === (size || null) && i.color === (color || null));
    if (!garmentInBag) addItem(product, size, qty, color);
    addAlteration(product, { id: "flat-15", label: "Custom tailoring", price: altFee?.price || 15 }, altFee?.variant_id);
    if (note) updateNote(`alt__${product.slug}__flat-15`, note);
    toast.success("Alteration added — your measurements are saved with your bag");
    setOpen(true);
  };

  const buyNow = () => {
    if (!validate()) return;
    addItem(product, size, qty, color);
    setOpen(true);
  };

  return (
    <div data-testid="product-detail-page" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-8">
      <Seo
        title={product.title}
        description={(product.description || `${product.title} — shop this Indian ethnic wear piece at Kurti Connection USA. In-store near Chicago, shipping nationwide.`).replace(/<[^>]+>/g, "").slice(0, 155)}
        path={`/product/${product.slug}`}
        image={product.images?.[0] || undefined}
        type="product"
        jsonLd={[
          productSchema(product),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Shop", path: "/shop" },
            { name: product.title, path: `/product/${product.slug}` },
          ]),
        ]}
      />
      <nav className="text-[0.7rem] font-mono tracking-wide text-[#6E6E69] mb-6">
        <Link to="/" className="hover:text-[#B84A3B]">Home</Link> / <Link to="/shop" className="hover:text-[#B84A3B]">Shop</Link> / <span className="text-[#1A1A1A]">{product.title}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14">
        {/* Gallery */}
        <div>
          {/* Desktop: thumbnails + main image */}
          <div className="hidden sm:flex gap-4">
            {product.images.length > 1 && (
              <div className="flex flex-col gap-3 w-20">
                {product.images.map((im, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} data-testid={`thumb-${i}`}
                    className={`aspect-[2/3] overflow-hidden border ${activeImg === i ? "border-[#1A1A1A]" : "border-[#E5E5DF]"}`}>
                    <img src={cdnImg(im, 160)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <motion.div key={activeImg} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
              className="flex-1 aspect-[2/3] overflow-hidden bg-[#EFEFEA]">
              <img src={cdnImg(product.images[activeImg], 900)} srcSet={cdnSrcSet(product.images[activeImg])} sizes="(max-width: 1024px) 90vw, 45vw" alt={product.title} decoding="async" className="w-full h-full object-cover" data-testid="main-image" />
            </motion.div>
          </div>

          {/* Mobile: swipeable carousel */}
          <div className="sm:hidden" data-testid="mobile-gallery">
            <div
              ref={galleryRef}
              onScroll={(e) => {
                const el = e.currentTarget;
                if (!el.clientWidth) return;
                const idx = Math.round(el.scrollLeft / el.clientWidth);
                if (Number.isFinite(idx) && idx !== activeImg) setActiveImg(idx);
              }}
              className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar -mx-5"
            >
              {product.images.map((im, i) => (
                <div key={i} className="snap-center shrink-0 w-screen px-5" data-testid={`mobile-slide-${i}`}>
                  <div className="aspect-[2/3] overflow-hidden bg-[#EFEFEA]">
                    <img src={cdnImg(im, 800)} srcSet={cdnSrcSet(im)} sizes="100vw" alt={product.title} loading={i === 0 ? "eager" : "lazy"} decoding="async" className="w-full h-full object-cover" />
                  </div>
                </div>
              ))}
            </div>
            {product.images.length > 1 && (
              <div className="flex justify-center gap-2 mt-3" data-testid="mobile-gallery-dots">
                {product.images.map((_, i) => (
                  <button
                    key={i}
                    aria-label={`View image ${i + 1}`}
                    data-testid={`mobile-dot-${i}`}
                    onClick={() => {
                      const el = galleryRef.current;
                      if (el) el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
                    }}
                    className={`h-1.5 rounded-full transition-all duration-300 ${activeImg === i ? "w-6 bg-[#1A1A1A]" : "w-1.5 bg-[#D6D6CE]"}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="lg:py-4">
          {product.badge && <div className="overline text-[#B84A3B] mb-2">{product.badge}</div>}
          <div className="flex items-start justify-between gap-4">
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none" data-testid="product-title">{product.title}</h1>
            <ShareMenu url={`${window.location.origin}/product/${product.slug}`} title={product.title} />
          </div>

          {product.reviews_count > 0 && (
            <div className="flex items-center gap-2 mt-3" data-testid="product-rating">
              <div className="flex text-[#B84A3B]">{[0, 1, 2, 3, 4].map((i) => <Star key={i} size={14} fill={i < Math.round(product.rating) ? "currentColor" : "none"} strokeWidth={1.5} />)}</div>
              <span className="text-[0.72rem] font-mono text-[#6E6E69]">{product.rating} · {product.reviews_count} review{product.reviews_count > 1 ? "s" : ""}</span>
            </div>
          )}

          <div className="flex items-center gap-3 mt-5">
            <span className="font-display text-3xl" data-testid="product-price">{money(product.price)}</span>
            {onSale && <><span className="text-lg text-[#B0B0A8] line-through">{money(product.compare_at)}</span>
              <span className="bg-[#B84A3B] text-white text-[0.65rem] font-mono px-2 py-1">{product.discount_pct}% OFF</span></>}
          </div>

          <p className="text-sm text-[#6E6E69] leading-relaxed mt-5">{product.description}</p>

          {colors.length > 0 && (
            <div className="mt-7">
              <div className="flex items-center gap-2 mb-3">
                <span className="overline text-[#1A1A1A]">Colour</span>
                <span className="text-[0.72rem] font-body text-[#6E6E69]">{color ? `— ${color}` : "— Select a colour"}</span>
              </div>
              <div className="flex flex-wrap items-center gap-3" data-testid="pd-color-options">
                {colors.map((c) => {
                  const avail = colorAvailable(c);
                  const selected = color === c;
                  return (
                    <button key={c} onClick={() => pickColor(c)} data-testid={`color-${c}`} disabled={!avail} title={c}
                      aria-label={c} aria-pressed={selected}
                      className={`relative w-9 h-9 rounded-full transition-transform duration-200 ${avail ? "hover:scale-110" : "opacity-40 cursor-not-allowed"}`}>
                      <span
                        className={`block w-full h-full rounded-full border ${isLightColor(c) ? "border-[#1A1A1A]/25" : "border-black/10"} ${selected ? "ring-2 ring-offset-2 ring-[#1A1A1A] ring-offset-[#FFFFFF]" : ""}`}
                        style={{ background: swatchBg(c) }}
                      />
                      {!avail && (
                        <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <span className="block w-[130%] h-px bg-[#1A1A1A]/60 rotate-45" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {sizes.length > 0 && (
            <div className="mt-7">
              <div className="flex items-center justify-between mb-2.5">
                <span className="overline text-[#1A1A1A]">Select {product.size_label || "Size"}</span>
                <button type="button" onClick={() => setSizeGuide(true)} data-testid="size-guide-trigger"
                  className="inline-flex items-center gap-1.5 border border-[#1A1A1A] px-3 py-1.5 text-[0.64rem] font-mono tracking-[0.12em] uppercase hover:bg-[#1A1A1A] hover:text-[#FFFFFF] transition-colors">
                  <Ruler size={13} strokeWidth={1.8} /> Size Guide
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {sizes.map((s) => {
                  const avail = sizeAvailable(s);
                  return (
                    <button key={s} onClick={() => pickSize(s)} data-testid={`size-${s}`} disabled={!avail}
                      className={`min-w-[3rem] px-3 h-11 border text-sm font-mono transition-colors ${
                        size === s ? "bg-[#1A1A1A] text-[#FFFFFF] border-[#1A1A1A]" : "border-[#E5E5DF] hover:border-[#1A1A1A]"
                      } ${!avail ? "opacity-40 line-through cursor-not-allowed hover:border-[#E5E5DF]" : ""}`}>{sizeLabel(s)}</button>
                  );
                })}
              </div>
              {mySize && (
                <p data-testid="pd-my-size" className={`mt-3 inline-flex items-center gap-2 px-3 py-1.5 text-[0.72rem] font-mono ${
                  mySizeFit ? "bg-[#2F5D3A]/10 text-[#2F5D3A]" : "bg-[#EFEFEA] text-[#6E6E69]"
                }`}>
                  {mySizeFit ? (
                    <><Check size={13} strokeWidth={2.5} /> Your size: {mySize.garment} · US {mySize.us} — this piece fits you</>
                  ) : (
                    <>Your quiz size: {mySize.garment} · US {mySize.us} — this piece comes in {sizes.map(sizeLabel).join(", ")}</>
                  )}
                  <Link to="/outfit-finder" className="underline underline-offset-2 hover:text-[#B84A3B] ml-1">Retake</Link>
                </p>
              )}
              <p className="mt-3 text-[0.72rem] text-[#6E6E69] font-body leading-relaxed" data-testid="pd-size-help">
                Every size shows the Indian number + US / age equivalent — e.g. <b className="text-[#1A1A1A]">Size 40 - US SMALL</b>, <b className="text-[#1A1A1A]">Size 24 - 3-4 years</b>.{" "}
                <button type="button" onClick={() => setSizeGuide(true)} className="underline underline-offset-2 hover:text-[#B84A3B]">View chart</button>. Unsure of your fit?{" "}
                <a href={`https://wa.me/12242461461?text=${encodeURIComponent(`Hi Kurti Connection! Can you help me with the size/measurements for "${product.title}"?`)}`}
                  target="_blank" rel="noreferrer" data-testid="pd-size-whatsapp"
                  className="text-[#1A8A45] underline underline-offset-2 hover:text-[#B84A3B]">Message us on WhatsApp</a> for exact measurements.
              </p>
            </div>
          )}

          {isPreorder ? (
            <div className="mt-7">
              <div ref={addRef} className="flex items-center gap-4">
                <div className="flex items-center border border-[#E5E5DF] h-12">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-dec" className="w-11 h-full flex items-center justify-center hover:bg-[#EFEFEA]"><Minus size={14} /></button>
                  <span className="w-10 text-center" data-testid="qty-value">{qty}</span>
                  <button onClick={() => setQty((q) => q + 1)} data-testid="qty-inc" className="w-11 h-full flex items-center justify-center hover:bg-[#EFEFEA]"><Plus size={14} /></button>
                </div>
                <a href={preorderWa} target="_blank" rel="noreferrer" data-testid="preorder-whatsapp-button"
                  className="flex-1 bg-[#25D366] text-[#0b3d1e] h-12 flex items-center justify-center text-[0.72rem] font-mono tracking-[0.18em] uppercase hover:opacity-90 transition-opacity">
                  Pre-Order on WhatsApp
                </a>
              </div>
              <p className="mt-3 text-[0.72rem] text-[#6E6E69] font-body leading-relaxed" data-testid="preorder-note">
                Made-to-order piece from our 2026–27 wedding collection — no online checkout. Message us with your sizes, quantities and wedding date.{" "}
                <Link to="/bridesmaid-dresses" className="underline underline-offset-2 hover:text-[#B84A3B]">Browse all bridesmaid styles</Link>
              </p>
            </div>
          ) : product.in_stock === false ? (
            <div className="mt-7">
              <button disabled data-testid="sold-out-button"
                className="w-full h-12 bg-[#EFEFEA] text-[#6E6E69] text-[0.72rem] font-mono tracking-[0.2em] uppercase cursor-not-allowed">
                Sold Out
              </button>
              <p className="mt-3 text-[0.72rem] text-[#6E6E69] font-body">
                This piece is sold out — single pieces go fast.{" "}
                <a href={`https://wa.me/12242461461?text=${encodeURIComponent(`Hi Kurti Connection! Is "${product.title}" coming back in stock?`)}`}
                  target="_blank" rel="noreferrer" data-testid="pd-restock-whatsapp"
                  className="text-[#1A8A45] underline underline-offset-2 hover:text-[#B84A3B]">Ask us on WhatsApp</a>{" "}
                — we may be able to restock or suggest something similar.
              </p>
            </div>
          ) : (
          <>
          <div ref={addRef} className="flex items-center gap-4 mt-7">
            <div className="flex items-center border border-[#E5E5DF] h-12">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-dec" className="w-11 h-full flex items-center justify-center hover:bg-[#EFEFEA]"><Minus size={14} /></button>
              <span className="w-10 text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} data-testid="qty-inc" className="w-11 h-full flex items-center justify-center hover:bg-[#EFEFEA]"><Plus size={14} /></button>
            </div>
            <button onClick={add} data-testid="add-to-cart-button"
              className="flex-1 bg-[#1A1A1A] text-[#FFFFFF] h-12 text-[0.72rem] font-mono tracking-[0.2em] uppercase hover:bg-[#B84A3B] transition-colors duration-300">
              Add to Cart · {money(product.price * qty)}
            </button>
          </div>
          {altFee?.available && alterationEligible(product) && (
            <button onClick={() => setAltOpen(true)} data-testid="add-alteration-button"
              className="mt-3 w-full h-11 border border-dashed border-[#B84A3B]/60 text-[#B84A3B] text-[0.7rem] font-mono tracking-[0.18em] uppercase hover:bg-[#B84A3B] hover:text-white transition-colors duration-300 flex items-center justify-center gap-2">
              <Scissors size={14} /> Add Alteration - $15 · We tailor to your measurements
            </button>
          )}
          <p className="flex items-center gap-2 mt-3 text-[0.72rem] text-[#1A1A1A] font-body" data-testid="pd-returns-reassurance">
            <RefreshCw size={13} className="text-[#B84A3B] flex-shrink-0" />
            <span><b className="font-semibold">Easy 14-day returns.</b> Ships fast from our Schaumburg, IL boutique.</span>
          </p>
          <p className="flex items-center gap-2 mt-2 text-[0.72rem] text-[#1A1A1A] font-body" data-testid="pd-doordash">
            <Zap size={13} className="text-[#B84A3B] flex-shrink-0" />
            <span><b className="font-semibold">Need it today?</b>{" "}
              <a href={SITE.doordash} target="_blank" rel="noopener noreferrer" data-testid="pd-doordash-link"
                className="underline underline-offset-2 text-[#B84A3B] hover:text-[#1A1A1A] transition-colors">Order on DoorDash</a>
              {" "}— same-day delivery within 25 miles of Schaumburg.</span>
          </p>
          <button onClick={buyNow} data-testid="buy-now-button"
            className="w-full mt-3 border border-[#1A1A1A] h-12 text-[0.72rem] font-mono tracking-[0.2em] uppercase hover:bg-[#1A1A1A] hover:text-[#FFFFFF] transition-colors duration-300">
            Buy It Now
          </button>
          </>
          )}
          <button onClick={() => setTryOn(true)} data-testid="try-on-button"
            className="w-full mt-3 flex items-center justify-center gap-2 h-12 text-[0.72rem] font-mono tracking-[0.2em] uppercase bg-gradient-to-r from-[#B84A3B] to-[#9c3a2d] text-white hover:from-[#1A1A1A] hover:to-[#1A1A1A] transition-all duration-300">
            <Sparkles size={15} /> Try It On · Virtually
          </button>
          <button onClick={() => toggle(product)} data-testid="wishlist-toggle-detail"
            className="w-full mt-3 flex items-center justify-center gap-2 h-12 text-[0.72rem] font-mono tracking-[0.2em] uppercase text-[#1A1A1A] hover:text-[#B84A3B] transition-colors duration-300">
            <Heart size={15} className={has(product.slug) ? "text-[#B84A3B]" : ""} fill={has(product.slug) ? "#B84A3B" : "none"} strokeWidth={1.8} />
            {has(product.slug) ? "Saved to Wishlist" : "Add to Wishlist"}
          </button>

          <a
            href={`https://wa.me/12242461461?text=${encodeURIComponent(`Hi Kurti Connection! I'm interested in "${product.title}" (${money(product.price)}).\n${window.location.origin}/product/${product.slug}\nCould you help me?`)}`}
            target="_blank"
            rel="noreferrer"
            data-testid="pd-whatsapp-button"
            onClick={() => trackContact("whatsapp")}
            className="w-full mt-3 flex items-center justify-center gap-2 border border-[#25D366] text-[#1A8A45] h-12 text-[0.72rem] font-mono tracking-[0.2em] uppercase hover:bg-[#25D366] hover:text-white transition-colors duration-300"
          >
            <svg viewBox="0 0 32 32" width="17" height="17" fill="currentColor" aria-hidden="true">
              <path d="M16.004 3C9.383 3 4 8.383 4 15.004c0 2.114.553 4.176 1.605 6l-1.7 6.207 6.36-1.668a11.94 11.94 0 0 0 5.74 1.46h.005C22.63 27.003 28 21.62 28 15.004 28 8.383 22.62 3 16.004 3zm0 21.6h-.004a9.9 9.9 0 0 1-5.05-1.383l-.362-.215-3.773.99 1.008-3.68-.236-.377a9.86 9.86 0 0 1-1.51-5.256c0-5.48 4.46-9.94 9.945-9.94 2.656 0 5.152 1.035 7.03 2.914a9.88 9.88 0 0 1 2.912 7.03c0 5.482-4.46 9.942-9.94 9.942zm5.453-7.44c-.297-.15-1.767-.872-2.04-.972-.273-.1-.472-.15-.67.15-.198.297-.767.97-.94 1.17-.173.198-.347.223-.644.074-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.76-1.653-2.058-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.15-.174.198-.298.298-.497.099-.198.05-.372-.025-.52-.074-.15-.67-1.612-.918-2.207-.242-.58-.487-.5-.67-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.478 0 1.462 1.065 2.874 1.213 3.073.149.198 2.096 3.2 5.076 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.767-.722 2.016-1.42.248-.697.248-1.294.173-1.42-.074-.124-.272-.198-.57-.347z"/>
            </svg>
            Ask on WhatsApp
          </a>



          <div className="flex flex-col gap-2 mt-6 text-[0.75rem] font-mono text-[#6E6E69]">
            {product.in_stock !== false && (
              <span className="flex items-center gap-2 text-[#1A1A1A]" data-testid="instore-availability">
                <MapPin size={14} className="text-[#1FA855]" /> In stock at our Schaumburg boutique — pick up today
              </span>
            )}
            <span className="flex items-center gap-2"><Truck size={14} className="text-[#B84A3B]" /> Free U.S. shipping over $75 · Ships Globally</span>
            <span className="flex items-center gap-2"><RefreshCw size={14} className="text-[#B84A3B]" /> Easy 14-day returns · Ships Globally</span>
          </div>

          <div className="mt-8">
            <Row label="Details">{product.fabric ? <>Fabric: {product.fabric}<br /></> : null}{product.color ? <>Colour: {product.color}</> : "Premium quality ethnic wear."}</Row>
            <Row label="Shipping & Returns">Free U.S. shipping on orders over $75. We ship across the USA &amp; Canada with fast delivery. Enjoy easy 14-day hassle-free returns.</Row>
            <Row label="Care">{isJewelry ? "Do not spray perfume or chemicals directly on the jewellery piece. Wipe gently with a soft dry cloth and store in a dry pouch, away from moisture." : "Dry clean only. Store folded in a cool, dry place. Handle embellishments with care."}</Row>
          </div>
        </div>
      </div>

      {/* Reviews (Judge.me — real product reviews, own styling) */}
      <ReviewSection product={product} />


      {completeLook.length > 0 && (
        <section className="mt-24" data-testid="complete-the-look">
          <div className="mb-8">
            <div className="overline text-[#B84A3B] mb-2">Style It Together</div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Complete the Look</h2>
            <p className="text-sm text-[#6E6E69] mt-2 font-body">Pair it with these to finish your outfit.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-10">
            {completeLook.map((p, i) => <ProductCard key={p.slug} product={p} index={i} onAdd={(pr, s) => addItem(pr, s)} />)}
          </div>
        </section>
      )}

      {related?.length > 0 && (
        <section className="mt-24" data-testid="you-may-also-like">
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-8">You May Also Like</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-10">
            {related.map((p, i) => <ProductCard key={p.slug} product={p} index={i} onAdd={(pr, s) => addItem(pr, s)} />)}
          </div>
        </section>
      )}

      <RecentlyViewed excludeSlug={slug} title="Recently Viewed" subtitle="Pieces you looked at earlier" />

      <TryOnModal open={tryOn} onOpenChange={setTryOn} product={product} onAddToCart={add} />
      <SizeGuideModal open={sizeGuide} onOpenChange={setSizeGuide} />
      <AlterationFeeModal
        open={altOpen}
        onClose={() => setAltOpen(false)}
        onConfirm={confirmFlatAlteration}
        productTitle={product ? product.title : ""}
      />

      {/* Sticky mobile buy bar */}
      <div
        data-testid="sticky-buy-bar"
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-[55] bg-white border-t border-[#E5E5DF] shadow-[0_-4px_20px_rgba(0,0,0,0.08)] transition-transform duration-300 ${showBar ? "translate-y-0" : "translate-y-full"}`}
      >
        <div className="bg-[#EFEFEA] px-4 py-1.5 text-center text-[0.62rem] font-mono tracking-wide text-[#1A1A1A]" data-testid="sticky-free-ship">
          {rawSubtotal >= 75
            ? <span className="text-[#B84A3B]">✓ You&apos;ve unlocked FREE U.S. shipping!</span>
            : rawSubtotal > 0
              ? <span>You&apos;re <b>{money(75 - rawSubtotal)}</b> away from FREE U.S. shipping</span>
              : <span>FREE U.S. shipping on orders over $75</span>}
        </div>
        <div className="flex items-center gap-3 px-4 py-3 pr-20">
          {product.images?.[0] && <img src={product.images[0]} alt="" className="w-11 h-14 object-cover flex-shrink-0" />}
          <div className="min-w-0 flex-1">
            <p className="text-xs font-body text-[#1A1A1A] truncate">{product.title}</p>
            <p className="text-sm font-mono text-[#B84A3B]">{money(product.price)}{onSale && <span className="ml-2 text-[0.7rem] text-[#6E6E69] line-through">{money(product.compare_at)}</span>}</p>
          </div>
          {isPreorder ? (
            <a href={preorderWa} target="_blank" rel="noreferrer" data-testid="sticky-preorder-whatsapp"
              className="flex-shrink-0 bg-[#25D366] text-[#0b3d1e] h-11 px-5 flex items-center text-[0.68rem] font-mono tracking-[0.15em] uppercase hover:opacity-90 transition-opacity">
              Pre-Order
            </a>
          ) : (
          <button onClick={add} data-testid="sticky-add-to-cart"
            className="flex-shrink-0 bg-[#1A1A1A] text-[#FFFFFF] h-11 px-5 text-[0.68rem] font-mono tracking-[0.15em] uppercase hover:bg-[#B84A3B] transition-colors">
            Add to Cart
          </button>
          )}
        </div>
      </div>
    </div>
  );
}
